import styled from "styled-components"
import { useCallback, useEffect, useState } from "react"
import type { ChangeEvent, FormEvent } from "react"
import api from "@utils/api.client"
import { trackEvent } from "@components/utils/tracking.client"

const CALLBACK_EVENT = "cta:open-callback"
const CALLBACK_ANCHOR_ID = "demander-un-rappel"
const CALLBACK_HASH = `#${CALLBACK_ANCHOR_ID}`

const Section = styled.section`
  position: relative;
  width: 100%;
  padding: clamp(4rem, 10vw, 7rem) 0;
  background: #081526;
  overflow: hidden;
  display: flex;
  justify-content: center;
`

const BackgroundImage = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 18% 22%, rgba(99, 195, 255, 0.22), transparent 58%),
    linear-gradient(120deg, rgba(8, 21, 38, 0.92), rgba(8, 21, 38, 0.55)),
    url("/img/cta-background.jpg") center 40% / cover no-repeat;
  opacity: 0.88;
  transform: scale(1.04);
  z-index: 0;

  @media (max-width: 768px) {
    background:
      linear-gradient(125deg, rgba(8, 21, 38, 0.94), rgba(8, 21, 38, 0.7)),
      url("/img/cta-background.jpg") center 35% / cover no-repeat;
    opacity: 0.94;
  }
`

const Grid = styled.div`
  position: relative;
  width: min(1100px, 92vw);
  display: grid;
  gap: 1px;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  align-items: stretch;
  z-index: 1;
`

const Panel = styled.article`
  background: rgba(8, 21, 38, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 2px;
  padding: clamp(1.8rem, 4vw, 2.4rem);
  display: grid;
  gap: 1.4rem;
  color: #ffffff;
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 50px rgba(8, 21, 38, 0.24);
`

const PrimaryPanel = styled(Panel)`
  position: relative;
  overflow: hidden;
  background: linear-gradient(145deg, rgba(10, 26, 46, 0.98) 0%, rgba(8, 21, 38, 0.88) 55%, rgba(10, 26, 46, 0.98) 100%);
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 30px 70px rgba(5, 15, 30, 0.55);

  &::before {
    content: "";
    position: absolute;
    top: -45%;
    right: -35%;
    width: 320px;
    height: 320px;
    background: radial-gradient(circle, rgba(99, 195, 255, 0.35), transparent 70%);
    opacity: 0.6;
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -55%;
    left: -40%;
    width: 260px;
    height: 260px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.12), transparent 70%);
    opacity: 0.55;
    pointer-events: none;
  }
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 2px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(99, 195, 255, 0.9);
  background: rgba(99, 195, 255, 0.12);
  border: 1px solid rgba(99, 195, 255, 0.35);
`

const Title = styled.h2`
  margin: 0.35rem 0 0.85rem;
  margin-top: 1rem;
  font-size: clamp(2rem, 5vw, 2.7rem);
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.01em;
`

const Subtitle = styled.p`
  margin: 0 0 0.9rem;
  font-size: 1.1rem;

  color: rgba(255, 255, 255, 0.84);
  line-height: 1.7;
  max-width: 340px;
`

const AccentLine = styled.span`
  display: block;
  width: 54px;
  height: 3px;
  border-radius: 0;
  background: linear-gradient(90deg, rgba(99, 195, 255, 0.9), rgba(99, 195, 255, 0));
`

const ActionStack = styled.div`
  display: grid;
  gap: 0.55rem;
  justify-items: start;

  @media (max-width: 768px) {
    justify-items: stretch;
  }
`

const CTAButton = styled.button`
  min-height: 52px;
  height: 52px;
  min-width: 240px;
  padding: 0 1.6rem;
  border-radius: 2px;
  border: 1px solid rgba(99, 195, 255, 0.35);
  background: linear-gradient(135deg, #3c8dd2 0%, #1b5f97 50%, #0d4172 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow:
    0 18px 40px rgba(13, 65, 114, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.28), transparent 65%);
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 24px 54px rgba(13, 65, 114, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.18);
  }

  &:hover::before {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid rgba(99, 195, 255, 0.7);
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

const SecondaryPanel = styled(Panel)`
  background: rgba(8, 21, 38, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.08);
`

const ColumnHeader = styled.p`
  margin: 0;
  font-size: 0.8rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
`

const ColumnText = styled.p`
  margin: 0;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.6;
`

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
`

const MetricCard = styled.div`
  border-radius: 2px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: grid;
  gap: 0.35rem;
  text-align: left;
`

const MetricValue = styled.span`
  font-size: 1.45rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
`

const MetricLabel = styled.span`
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.65);
`

const ContactBlock = styled.div`
  display: grid;
  gap: 0.5rem;
  font-size: 0.98rem;

  a {
    color: #63c3ff;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`

const FormOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 17, 31, 0.84);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 20;
`

const FormCard = styled.div`
  width: min(520px, 92vw);
  background: #0c1f36;
  border-radius: 2px;
  border-top: 4px solid #63c3ff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 28px 70px rgba(8, 21, 38, 0.6);
  padding: 1.8rem;
  display: grid;
  gap: 1rem;
  position: relative;
`

const FormClose = styled.button`
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.4rem;
  cursor: pointer;

  &:hover {
    color: #ffffff;
  }
`

const FormLead = styled.p`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
`

const FormFields = styled.form`
  display: grid;
  gap: 0.9rem;
`

const FieldGroup = styled.label`
  display: grid;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
`

const FieldInput = styled.input`
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 0.75rem 0.9rem;
  font-size: 0.95rem;

  &:focus {
    outline: 2px solid rgba(99, 195, 255, 0.6);
  }
`

const FieldSelect = styled.select`
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 0.75rem 0.9rem;
  font-size: 0.95rem;

  &:focus {
    outline: 2px solid rgba(99, 195, 255, 0.6);
  }

  option {
    color: #0c1f36;
  }
`

const SubmitButton = styled.button`
  min-height: 48px;
  border-radius: 2px;
  border: none;
  background: linear-gradient(135deg, #63c3ff 0%, #1b75bc 55%, #0d4172 100%);
  color: #081526;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(99, 195, 255, 0.3);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    box-shadow: none;
  }
`

const LegalNote = styled.small`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.65);
`

const FormError = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #ffb4b4;
`

const SuccessMessage = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
`

const initialFormState = {
  phone: "",
  sector: "",
  unitRange: "",
  mainIssue: "",
}

const normalizePhone = (value: string) => {
  const digitsOnly = value.replace(/\D/g, "")
  if (digitsOnly.length > 10 && digitsOnly.startsWith("1")) {
    return digitsOnly.slice(1, 11)
  }
  return digitsOnly.slice(0, 10)
}

const formatPhone = (digits: string) => {
  const cleaned = digits.replace(/\D/g, "")
  const area = cleaned.slice(0, 3)
  const exchange = cleaned.slice(3, 6)
  const line = cleaned.slice(6, 10)

  if (cleaned.length <= 3) {
    return area
  }
  if (cleaned.length <= 6) {
    return `(${area}) ${exchange}`
  }
  return `(${area}) ${exchange}-${line}`
}

export const CTASecondary = () => {
  const [isFormOpen, setFormOpen] = useState(false)
  const [formData, setFormData] = useState(initialFormState)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target
    if (name === "phone") {
      const normalizedPhone = normalizePhone(value)
      setFormData((prev) => ({ ...prev, phone: normalizedPhone }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }

    if (errorMessage) {
      setErrorMessage(null)
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrorMessage(null)

    if (formData.phone.length !== 10) {
      setErrorMessage("Veuillez entrer un numéro de téléphone à 10 chiffres.")
      return
    }

    setIsSubmitting(true)

    try {
      await api.post("/callback-request", {
        phone: formData.phone,
        sector: formData.sector,
        unitRange: formData.unitRange || undefined,
        mainIssue: formData.mainIssue || undefined,
      })
      trackEvent("callback_request_submit", { form_name: "rappel_accueil" })
      setIsSuccess(true)
      setFormData(initialFormState)
    } catch {
      setErrorMessage("Une erreur est survenue. Veuillez réessayer ou nous appeler au (418) 684-9004.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const openForm = useCallback(() => {
    setFormOpen(true)
    setIsSuccess(false)
    setErrorMessage(null)
  }, [])

  const closeForm = useCallback(() => {
    setFormOpen(false)
    setIsSuccess(false)
    setErrorMessage(null)
  }, [])

  useEffect(() => {
    const handleOpen = () => openForm()
    window.addEventListener(CALLBACK_EVENT, handleOpen)

    if (window.location.hash === CALLBACK_HASH) {
      openForm()
    }

    return () => window.removeEventListener(CALLBACK_EVENT, handleOpen)
  }, [openForm])

  return (
    <Section id={CALLBACK_ANCHOR_ID}>
      <BackgroundImage aria-hidden="true" />
      <Grid>
        <PrimaryPanel>
          <div>
            <Badge>Demande de rappel</Badge>
            <Title>Entrer en contact</Title>
            <Subtitle>Laissez vos coordonnées et le contexte utile. Notre équipe vous recontactera pendant les heures d’ouverture.</Subtitle>
            <AccentLine aria-hidden="true" />
          </div>
          <ActionStack>
            <CTAButton type="button" onClick={openForm}>
              Demander un rappel
            </CTAButton>
          </ActionStack>
        </PrimaryPanel>

        <SecondaryPanel>
          <ColumnHeader>Pourquoi nous choisir ?</ColumnHeader>
          <ColumnText>Un premier échange simple pour comprendre votre situation.</ColumnText>
          <Metrics>
            <MetricCard>
              <MetricValue>7/7</MetricValue>
              <MetricLabel>Ligne d’urgence opérationnelle</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>Personne-ressource attitrée</MetricValue>
              <MetricLabel>Gestion locale et personnalisée</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>Espace TB</MetricValue>

              <MetricLabel>Suivi transparent et accessible</MetricLabel>
            </MetricCard>
          </Metrics>
          <ContactBlock>
            <span>Préférez un échange direct ?</span>
            <a href="tel:4186849004">(418) 684-9004</a>
            <a href="mailto:info@tbgestionimmobiliere.com">info@tbgestionimmobiliere.com</a>
          </ContactBlock>
        </SecondaryPanel>
      </Grid>

      {isFormOpen && (
        <FormOverlay onClick={closeForm}>
          <FormCard role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <FormClose type="button" onClick={closeForm} aria-label="Fermer le formulaire">
              ×
            </FormClose>
            {!isSuccess && <FormLead>À quel numéro et pour quel immeuble devons-nous vous rappeler?</FormLead>}
            {isSuccess ? (
              <SuccessMessage>Merci! Votre demande de rappel a été enregistrée avec succès.</SuccessMessage>
            ) : (
              <FormFields onSubmit={handleSubmit}>
                <FieldGroup>
                  Téléphone (obligatoire)
                  <FieldInput
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(418) 555-1234"
                    value={formatPhone(formData.phone)}
                    onChange={handleChange}
                    required
                  />
                </FieldGroup>
                <FieldGroup>
                  Ville / secteur (obligatoire)
                  <FieldInput
                    name="sector"
                    type="text"
                    placeholder="Neuville, Donnacona, Cap-Santé..."
                    value={formData.sector}
                    onChange={handleChange}
                    required
                  />
                </FieldGroup>
                <FieldGroup>
                  Nombre de logements
                  <FieldSelect name="unitRange" value={formData.unitRange} onChange={handleChange}>
                    <option value="">Sélectionner</option>
                    <option value="2-5">2–5</option>
                    <option value="6-12">6–12</option>
                    <option value="13-30">13–30</option>
                    <option value="30+">30+</option>
                  </FieldSelect>
                </FieldGroup>
                <FieldGroup>
                  Enjeu principal
                  <FieldSelect name="mainIssue" value={formData.mainIssue} onChange={handleChange}>
                    <option value="">Sélectionner</option>
                    <option value="vacance">Vacance</option>
                    <option value="entretien">Entretien</option>
                    <option value="locataires">Locataires</option>
                    <option value="administratif">Administratif</option>
                    <option value="rendement">Rendement</option>
                    <option value="autre">Autre</option>
                  </FieldSelect>
                </FieldGroup>
                {errorMessage && <FormError>{errorMessage}</FormError>}
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Envoi en cours..." : "Demander un rappel"}
                </SubmitButton>
                <LegalNote>Aucun spam. Vos infos servent uniquement à vous rappeler.</LegalNote>
              </FormFields>
            )}
          </FormCard>
        </FormOverlay>
      )}
    </Section>
  )
}
